import { getAllParamValues } from "@pepsico/pref-permissions";

import { PLOT_GRID_CYAN } from "context/PlotMap/gridUtils";
import { LAYERS_PREF_KEY, type PlotMapLayer } from "defs";

import {
  BOUNDARY_COLOR,
  GROWER_LABEL_BG_COLOR,
  LABEL_FILL_COLOR,
  PLOT_LABEL_BG_COLOR,
  ROW_BOUNDARY_COLOR,
  ROW_LABEL_BG_COLOR,
  SITE_BOUNDARY_COLOR,
  SITE_LABEL_BG_COLOR,
} from "./mapConstants";

export const ORTHOPHOTO_IMAGE_LAYER_ID = "odm-orthophoto-overview-raster";
export const ORTHOPHOTO_OUTLINE_LAYER_ID = "odm-orthophoto-outline-line";

export const BOUNDARY_LINE_LAYER_ID = "plot-boundary-line";
export const BOUNDARY_LABEL_LAYER_ID = "plot-boundary-label";
export const BOUNDARY_VERTEX_LAYER_ID = "plot-boundary-vertices";
export const BOUNDARY_FIRST_POINT_LAYER_ID = "plot-boundary-first-point";
// Growers and sites are both lists: the static source keeps other saved
// polygons visible while these draft layers show the in-progress ring.
export const GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID =
  "plot-grower-boundary-draft-line";
export const GROWER_BOUNDARY_DRAFT_LABEL_LAYER_ID =
  "plot-grower-boundary-draft-label";

export const SITE_BOUNDARY_LINE_LAYER_ID = "plot-site-boundary-line";
export const SITE_BOUNDARY_LABEL_LAYER_ID = "plot-site-boundary-label";
export const SITE_BOUNDARY_DRAFT_LINE_LAYER_ID =
  "plot-site-boundary-draft-line";
export const SITE_BOUNDARY_DRAFT_LABEL_LAYER_ID =
  "plot-site-boundary-draft-label";
export const SITE_BOUNDARY_VERTEX_LAYER_ID = "plot-site-boundary-vertices";
export const SITE_BOUNDARY_FIRST_POINT_LAYER_ID =
  "plot-site-boundary-first-point";

export const GRID_BBOX_LAYER_ID = "plot-grid-bbox-line";
export const GRID_CELL_LAYER_ID = "plot-grid-cell";
export const GRID_HANDLE_CORNER_LAYER_ID = "plot-grid-handle-corner";
export const GRID_LABEL_LAYER_ID = "plot-grid-label";
export const GRID_TRIAL_LABEL_LAYER_ID = "plot-grid-trial-label";
export const GRID_LINE_LAYER_ID = "plot-grid-line";
export const GRID_ROW_LABEL_LAYER_ID = "plot-grid-row-label";
export const GRID_ROW_LINE_LAYER_ID = "plot-grid-row-line";
export const GRID_VERTEX_LAYER_ID = "plot-grid-vertices";

export const ROW_FILL_LAYER_ID = "plot-row-fill";
export const ROW_BOUNDARY_LABEL_LAYER_ID = "plot-row-boundary-label";
export const ROW_BOUNDARY_LINE_LAYER_ID = "plot-row-boundary-line";

// Read-only plots for trials other than the one being edited.
export const SAVED_PLOTS_FILL_LAYER_ID = "plot-saved-plots-fill";
export const SAVED_PLOTS_LABEL_LAYER_ID = "plot-saved-plots-label";
export const SAVED_PLOTS_LINE_LAYER_ID = "plot-saved-plots-line";
export const SAVED_PLOTS_TRIAL_LABEL_LAYER_ID = "plot-saved-plots-trial-label";

/** #ffeb3b on this gray is 4.56:1, which meets WCAG AA for text and graphics. */
const YELLOW_SWATCH_BACKGROUND = "#686868";
/** Gray outside the yellow stroke so the menu's white does not touch it. */
const YELLOW_SWATCH_OUTSET = `0 0 0 3px ${YELLOW_SWATCH_BACKGROUND}`;

type PlotMapEditMode = "grower" | "plot" | "site";

interface PlotMapLayerConfig {
  /** Present when an edit mode keeps this layer visible. */
  editLock?: {
    mode: PlotMapEditMode;
    tooltip: string;
  };
  label: string;
  mapboxLayerIds: readonly string[];
  swatchSx: object;
}

const PLOT_EDIT_LOCK_TOOLTIP =
  "Plot and row layers stay visible while editing a plot grid.";

/** Pref code → UI + Mapbox metadata. Presence in `layers` pref = visible. */
export const PLOT_MAP_LAYERS: Record<PlotMapLayer, PlotMapLayerConfig> = {
  bnd: {
    editLock: {
      mode: "grower",
      tooltip:
        "The grower boundary stays visible while editing a grower boundary.",
    },
    label: "Grower Boundary",
    mapboxLayerIds: [
      BOUNDARY_LINE_LAYER_ID,
      GROWER_BOUNDARY_DRAFT_LINE_LAYER_ID,
      BOUNDARY_VERTEX_LAYER_ID,
      BOUNDARY_FIRST_POINT_LAYER_ID,
    ],
    swatchSx: {
      bgcolor: "transparent",
      border: `2px solid ${BOUNDARY_COLOR}`,
    },
  },
  glbl: {
    label: "Grower Labels",
    mapboxLayerIds: [
      BOUNDARY_LABEL_LAYER_ID,
      GROWER_BOUNDARY_DRAFT_LABEL_LAYER_ID,
    ],
    swatchSx: {
      "&::before": {
        content: '"#"',
      },
      alignItems: "center",
      bgcolor: GROWER_LABEL_BG_COLOR,
      color: LABEL_FILL_COLOR,
      display: "inline-flex",
      fontSize: 12,
      fontWeight: 700,
      justifyContent: "center",
      lineHeight: 1,
    },
  },
  site: {
    editLock: {
      mode: "site",
      tooltip: "Site boundaries stay visible while editing a site boundary.",
    },
    label: "Site Boundaries",
    mapboxLayerIds: [
      SITE_BOUNDARY_LINE_LAYER_ID,
      SITE_BOUNDARY_DRAFT_LINE_LAYER_ID,
      SITE_BOUNDARY_VERTEX_LAYER_ID,
      SITE_BOUNDARY_FIRST_POINT_LAYER_ID,
    ],
    swatchSx: {
      bgcolor: YELLOW_SWATCH_BACKGROUND,
      border: `2px solid ${SITE_BOUNDARY_COLOR}`,
      boxShadow: YELLOW_SWATCH_OUTSET,
    },
  },
  slbl: {
    label: "Site Labels",
    mapboxLayerIds: [
      SITE_BOUNDARY_LABEL_LAYER_ID,
      SITE_BOUNDARY_DRAFT_LABEL_LAYER_ID,
    ],
    swatchSx: {
      "&::before": {
        content: '"#"',
      },
      alignItems: "center",
      bgcolor: SITE_LABEL_BG_COLOR,
      color: LABEL_FILL_COLOR,
      display: "inline-flex",
      fontSize: 12,
      fontWeight: 700,
      justifyContent: "center",
      lineHeight: 1,
    },
  },
  grid: {
    editLock: {
      mode: "plot",
      tooltip: PLOT_EDIT_LOCK_TOOLTIP,
    },
    label: "Plot Grid",
    mapboxLayerIds: [
      GRID_CELL_LAYER_ID,
      GRID_LINE_LAYER_ID,
      GRID_TRIAL_LABEL_LAYER_ID,
      GRID_HANDLE_CORNER_LAYER_ID,
      GRID_VERTEX_LAYER_ID,
      GRID_BBOX_LAYER_ID,
      SAVED_PLOTS_FILL_LAYER_ID,
      SAVED_PLOTS_LINE_LAYER_ID,
      SAVED_PLOTS_TRIAL_LABEL_LAYER_ID,
    ],
    swatchSx: {
      bgcolor: "transparent",
      border: `2px solid ${PLOT_GRID_CYAN}`,
    },
  },
  lbl: {
    label: "Plot Labels",
    mapboxLayerIds: [GRID_LABEL_LAYER_ID, SAVED_PLOTS_LABEL_LAYER_ID],
    swatchSx: {
      "&::before": {
        content: '"#"',
      },
      alignItems: "center",
      bgcolor: PLOT_LABEL_BG_COLOR,
      color: LABEL_FILL_COLOR,
      display: "inline-flex",
      fontSize: 12,
      fontWeight: 700,
      justifyContent: "center",
      lineHeight: 1,
    },
  },
  row: {
    editLock: {
      mode: "plot",
      tooltip: PLOT_EDIT_LOCK_TOOLTIP,
    },
    label: "Row Boundaries",
    mapboxLayerIds: [ROW_BOUNDARY_LINE_LAYER_ID, GRID_ROW_LINE_LAYER_ID],
    swatchSx: {
      bgcolor: YELLOW_SWATCH_BACKGROUND,
      border: `2px dashed ${ROW_BOUNDARY_COLOR}`,
      boxShadow: YELLOW_SWATCH_OUTSET,
    },
  },
  rlbl: {
    label: "Row Labels",
    mapboxLayerIds: [GRID_ROW_LABEL_LAYER_ID, ROW_BOUNDARY_LABEL_LAYER_ID],
    swatchSx: {
      "&::before": {
        content: '"#"',
      },
      alignItems: "center",
      bgcolor: ROW_LABEL_BG_COLOR,
      color: ROW_BOUNDARY_COLOR,
      display: "inline-flex",
      fontSize: 12,
      fontWeight: 700,
      justifyContent: "center",
      lineHeight: 1,
    },
  },
  orth: {
    label: "Orthomosaic",
    mapboxLayerIds: [ORTHOPHOTO_IMAGE_LAYER_ID, ORTHOPHOTO_OUTLINE_LAYER_ID],
    swatchSx: {
      backgroundImage: [
        "radial-gradient(circle at 18% 28%, #7cb342 0 28%, transparent 30%)",
        "radial-gradient(circle at 72% 22%, #558b2f 0 22%, transparent 24%)",
        "radial-gradient(circle at 42% 68%, #9ccc65 0 26%, transparent 28%)",
        "radial-gradient(circle at 78% 74%, #33691e 0 24%, transparent 26%)",
        "radial-gradient(circle at 30% 48%, #689f38 0 18%, transparent 20%)",
        "linear-gradient(135deg, #558b2f, #2e7d32 45%, #1b5e20)",
      ].join(", "),
    },
  },
};

/** Layers menu order. */
export const PLOT_MAP_LAYER_ORDER = [
  "bnd",
  "site",
  "orth",
  "grid",
  "row",
] as const satisfies readonly PlotMapLayer[];

const isPlotMapLayer = (value: string): value is PlotMapLayer =>
  Object.hasOwn(PLOT_MAP_LAYERS, value);

export const isPlotMapLayerVisible = (
  prefs: Record<string, string | string[]>,
  layer: PlotMapLayer,
): boolean => getAllParamValues(prefs, LAYERS_PREF_KEY).includes(layer);

/** Grower labels require the grower-boundary layer as well as their own pref. */
export const isGrowerLabelLayerVisible = (
  prefs: Record<string, string | string[]>,
): boolean =>
  isPlotMapLayerVisible(prefs, "bnd") && isPlotMapLayerVisible(prefs, "glbl");

/** Plot labels require the parent plot-grid layer as well as their own pref. */
export const isPlotLabelLayerVisible = (
  prefs: Record<string, string | string[]>,
): boolean =>
  isPlotMapLayerVisible(prefs, "grid") && isPlotMapLayerVisible(prefs, "lbl");

/** Row labels require the parent row-boundary layer as well as their own pref. */
export const isRowLabelLayerVisible = (
  prefs: Record<string, string | string[]>,
): boolean =>
  isPlotMapLayerVisible(prefs, "row") && isPlotMapLayerVisible(prefs, "rlbl");

/** Site labels require the site-boundary layer as well as their own pref. */
export const isSiteLabelLayerVisible = (
  prefs: Record<string, string | string[]>,
): boolean =>
  isPlotMapLayerVisible(prefs, "site") && isPlotMapLayerVisible(prefs, "slbl");

export const toMapboxVisibility = (isVisible: boolean): "visible" | "none" =>
  isVisible ? "visible" : "none";

export const togglePlotMapLayer = (
  prefs: Record<string, string | string[]>,
  layer: PlotMapLayer,
): PlotMapLayer[] => {
  const current = getAllParamValues(prefs, LAYERS_PREF_KEY).filter(
    isPlotMapLayer,
  );

  if (current.includes(layer)) {
    return current.filter((entry) => entry !== layer);
  }

  return [...current, layer];
};

/** Adds any of `layers` that are hidden. Leaves visible layers unchanged. */
export const showPlotMapLayers = (
  prefs: Record<string, string | string[]>,
  layers: readonly PlotMapLayer[],
): PlotMapLayer[] => {
  const current = getAllParamValues(prefs, LAYERS_PREF_KEY).filter(
    isPlotMapLayer,
  );
  const missing = layers.filter((layer) => !current.includes(layer));

  return [...current, ...missing];
};
